/**
 * Decider for "equal priority" policy.
 * In equal priority campaigns, experiments are grouped into priority levels. A visitor will be bucketed into
 * an experiment in the first priority level if they fit into the audiences for any experiment on that level.
 * Experiment priorities is an array of arrays of weightDistributions. If endOfRange in the weight
 * distributions is null, experiments will be equally weighted. Otherwise, final weight distribution will be
 * calculated by normalizing the distribution among the experiments for which the visitor qualifies, based on
 * those experiments' original weights and the number of such experiments.
 * If a visitor is not eligible for any experiments in a given tier, they will fall through to
 * the next level, and attempt to be bucketed there. Once bucketed into an experiment, variation bucketing works
 * normally (i.e., random non-sticky bucketing depending on variation weights).
 * If decisionMetadata.offerConsistency is true for the layer, and the variation has no actions for the current view, then the
 * visitor will fall through to the next priority level, and a new experiment and variation will be selected.
 *
 *
 * @example
 * ```js
 * var layer = {
 *  experiments: [{
 *     id: '1',
 *     audienceIds: ['10', '20'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }, {
 *     id: '2',
 *     audienceIds: ['10', '40'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }, {
 *     id: '3',
 *     audienceIds: ['10'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }, {
 *     id: '4',
 *     audienceIds: ['5'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }, {
 *     id: '5',
 *     audienceIds: ['5', '6'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }],
 *   decisionMetadata: {
 *     experimentPriorities: [ [ {entityId: 1, endOfRange: 2500}, {entityId: 3, endOfRange: 10000} ],
 *       [ {entityId: 2, endOfRange: 7000}, {entityId: 4, endOfRange: 8000}, {entityId: 5, endOfRange: 10000} ] ]
 *   }
 * };
 * ```
 * A visitor fitting only in audience 10 would have a 25% chance of seeing experiment 1, and a 75% chance of seeing experiment 3.
 * A visitor fitting audience 20 would be bucketed into experiment 1.
 * A visitor fitting audience 6 would be bucketed into experiment 5.
 *
 * Update on decisionMetadata - 25 Sep, 2024:
 * A new property `experimentWeights` is introduced where, if it exists, will be used to allocate traffic between experiments in a group.
 *
 * @example
 * ```js
 * var layer = {
 *  experiments: [{
 *     id: '1',
 *     audienceIds: ['10', '20'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }, {
 *     id: '2',
 *     audienceIds: ['10', '11'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }, {
 *     id: '3',
 *     audienceIds: ['10'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }, {
 *     id: '4',
 *     audienceIds: ['5', '11'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }, {
 *     id: '5',
 *     audienceIds: ['5', '6', '11'],
 *     variations: [...],
 *     weightDistributions: [...],
 *   }],
 *   decisionMetadata: {
 *     experimentPriorities: [ [1, 3], [2, 4, 5] ],
 *     experimentWeights: {
 *       1: 2000,
 *       2: 2000,
 *       3, 8000,
 *       4: 3000,
 *       5: 5000,
 *     }
 *   }
 * };
 * ```
 * - A visitor fitting only in audience 10 would have a 20% chance of seeing experiment 1, and 80% chance of seeing experiment 3.
 * - A visitor fitting audience 6 would be bucketed into experiment 5. The weight would have been 50% chance, but as it is the only experiment the user is allowed by audience conditions, it will be normalized to 100%.
 * - A visitor fitting audience 11 would have a 20% chance of seeing experiment 2, 30% chance of seeing experiment 4 and 50% chance of seeing experiment 5 without any normalization required.
 * - A visitor fitting audience 5 would have a 37.5% chance of seeing experiment 4 and 62.5% chance of seeing experiment 5, after normalizing the experimentWeights.
 *
 * @module
 */


var _ = require('optimizely-client-fn');
var bucketing = require('core/decision/bucketing');
var deciderFns = require('core/decision/decider_fns');
var DecisionError = require('core/decision/error').DecisionError;
var Logger = require('core/utils/logger');

/**
 * @constant
 * @type {string}
 */
var EQUAL_PRIORITY = 'equal_priority';

var pluginDeciderEqualPriority: Decider = {
  /**
   * Decide the visitor's experiment and variation
   * @param {Layer} layer
   * @param {DecisionTicket} decisionTicket
   * @returns {Object} decision with optional experiment, variation, and error properties
   */
  decideLayer: function(layer:Layer, decisionTicket:DecisionTicket): any {
    if (!layer.decisionMetadata || !layer.decisionMetadata.experimentPriorities) {
      throw new DecisionError('No decisionMetadata.experimentPriorities on layer.');
    }

    var priorityLevels = layer.decisionMetadata.experimentPriorities.length;

    for (var i = 0; i < priorityLevels; i++) {
      var selectedExperiment = selectExperiment(
        layer, decisionTicket.audienceIds, decisionTicket.bucketingId, i);
      if (!selectedExperiment) {
        // No experiment for current priority level
        continue;
      }
      var selectedVariation = deciderFns.selectVariation(
          selectedExperiment, decisionTicket.audienceIds, decisionTicket.bucketingId, decisionTicket.activationId, decisionTicket.preferredVariationMap);

      if (!layer.decisionMetadata.offerConsistency || deciderFns.hasVariationActionsOnView(selectedVariation, decisionTicket.pageId)) {
        return {
          experiment: selectedExperiment,
          variation: selectedVariation,
        };
      }
    }
    throw new DecisionError('No eligible experiment and variation found.');
  },

  /**
   * @param {Layer} layer
   * @returns {boolean}
   */
  includePageIdInDecisionTicket: function(layer:Layer): boolean {
    if (layer.decisionMetadata && layer.decisionMetadata.offerConsistency) {
      return true;
    }
    return false;
  },
};

/**
 * Decide which experiment the visitor should be bucketed into. Experiment IDs not included in
 * experimentPriorities are implicitly ignored.
 * @param {Layer} layer - Layer from decisionTicket
 * @param {string[]} audienceIds - Visitor's audienceIds from decisionTicket
 * @param {string} bucketingId - BucketingId from decisionTicket
 * @param {number} priorityLevel - Which experiment priority level to check
 * @param {experimentPriorities} layer.decisionMetadata.experimentPriorities Array of arrays showing experiment priorities (and optionally weight distributions)
 * @returns {Object} An object with the selected experiment
 * @private
 */
function selectExperiment(layer:Layer, audienceIds:string[], bucketingId:string, priorityLevel:number): any {
  var candidatesInPriorityLevel = layer.decisionMetadata.experimentPriorities[priorityLevel];
  var selectedExperimentId;

  var weights = layer.decisionMetadata?.experimentWeights;

  var filterCandidates = function(res, item) {
    var experiment = deciderFns.getExperimentById(layer, item.entityId);
    if (deciderFns.isValidExperiment(audienceIds, experiment)) {
      res.push(item.entityId);
    }
    return res;
  };

  var candidates = _.reduce(candidatesInPriorityLevel, filterCandidates, []);

  // If any experiment in priority level matches audiences, bucket visitor
  if (!_.isEmpty(candidates)) {
    var normalizedDistributions;
    var nullWeights = _.filter(candidatesInPriorityLevel, {endOfRange: null});
    if (nullWeights && nullWeights.length === candidatesInPriorityLevel.length) {
      normalizedDistributions = getEqualWeights(candidates);
      if (!_.isEmpty(weights) && __ALLOCATE_PRIORITIZED_EXPERIENCE_TRAFFIC__) {
        try {
          const selectedWeights = _.pick(weights, candidates);
          normalizedDistributions = getAllocatedWeights(candidates, selectedWeights);
        }
        catch (err) {
          Logger.warn('Decider / Could not allocate user-defined weights', err);
        }
      }
    } else if (nullWeights.length === 0) {
      normalizedDistributions = getNormalizedDistributions(candidates, candidatesInPriorityLevel);
    } else {
      throw new DecisionError(
        'You must specify weights for all or none of the experiments in each priority layer.'
      );
    }

    selectedExperimentId = bucketing.chooseWeightedCandidate(bucketingId, layer.id, normalizedDistributions);
    if (selectedExperimentId) {
      var selectedExperiment = deciderFns.getExperimentById(layer, selectedExperimentId);
      if (selectedExperiment) {
        return selectedExperiment;
      }
    }
  }
}

/**
 * Utility function to create weight distributions for priority layers where weights are specified in `experimentWeights`.
 * If weights don't add up to 10000, the weights will be normalized
 * In case a weight for a specific ID does not exist, it will throw an error
 * @private
 * @param {Array.<string>} experimentIds of experimentIds where visitor matches at least one audience
 * @param {ExperimentWeights} experimentWeights object containing weight of each experiment in key-value pair
 * @returns {WeightDistribution[]} array of weight distributions for the experiments
 */
function getAllocatedWeights(experimentIds: string[], experimentWeights: ExperimentWeights): WeightDistribution[] {
  let accumulatedWeight = 0;
  // This will be used to determine whether weight normalization to 10000 is required
  const weightSum = _.reduce(_.values(experimentWeights), (acc: number, value: number) => acc + value, 0);

  if (weightSum > 10000) {
    throw new DecisionError(
        `Weight distribution is invalid. Total weights: ${weightSum}`
    );
  }

  const distributions: WeightDistribution[] = _.map(experimentIds, (experimentId: number, index) => {
    let experimentWeight = experimentWeights[experimentId];
    if (!_.isNumber(experimentWeight)) {
      throw new DecisionError( // throw an error because the experiment is considered for bucketing but its weight does not exist
          `Weight distribution for Experiment ID ${experimentId} not found.`
      );
    }
    if (weightSum < 10000) { // normalization happens in this code-block
      if (index === experimentIds.length - 1) {
        experimentWeight = 10000 - accumulatedWeight;
      } else {
        experimentWeight = Math.floor((experimentWeights[experimentId] / weightSum) * 10000);
      }
    }

    accumulatedWeight += experimentWeight;
    return {
      entityId: experimentId,
      endOfRange: accumulatedWeight,
    };
  });
  return distributions;
}

/**
 * Utility function to create equal weight distributions for priority layers where weights are not specified.
 * @private
 * @param {Array.<string>} experimentIds of experimentIds where visitor matches at least one audience
 * @returns {WeightDistribution} array of weight distributions for the experiments
 * @private
 */
function getEqualWeights(experimentIds:string[]): WeightDistribution[] {
  var distributions = [];
  var intervals = Math.round(10000 / experimentIds.length);
  for (var k = 0; k < experimentIds.length; k++) {
    distributions.push({
      entityId: experimentIds[k],
      endOfRange: intervals * (k + 1),
    });
  }
  // Make sure distributions ends at 10000 (TODO: This is bad if there are a lot of entities)
  distributions[distributions.length - 1].endOfRange = 10000;
  return distributions;
}

/**
 * Utility function to create the weight distributions for matching experiments in a priority layer
 * @private
 * @param {Array.<string>} experimentIds of experimentIds where visitor matches at least one audience
 * @param {Array.<WeightDistribution>} weightDistributions describing the weight distributions for a priority level
 * @returns {WeightDistribution} array of weight distributions for the experiments
 * @private
 */
function getNormalizedDistributions(experimentIds:string[], weightDistributions:WeightDistribution[]): WeightDistribution[] {
  var distributions = [];
  var values = [];
  var total = 0;

  // Get the new "total" points existing
  for (var i = 0; i < weightDistributions.length; i++) {
    var points = 0;
    if (experimentIds.indexOf(weightDistributions[i].entityId) >= 0) {
      if (i === 0) {
        points = weightDistributions[0].endOfRange;
      } else {
        points = weightDistributions[i].endOfRange - weightDistributions[i - 1].endOfRange;
      }
      total += points;
      values.push({entityId: weightDistributions[i].entityId, points: points});
    }
  }

  // Normalize weight distributions
  if (_.isEmpty(distributions)) {
    var lastEndOfRange = 0;
    for (var j = 0; j < values.length; j++) {
      var newEndOfRange = Math.round(values[j].points * 10000 / total) + lastEndOfRange;
      distributions.push({entityId: values[j].entityId, endOfRange: newEndOfRange});
      lastEndOfRange = newEndOfRange;
    }
  }

  // Make sure distributions ends at 10000 (TODO: This is bad if there are a lot of entities)
  distributions[distributions.length - 1].endOfRange = 10000;
  return distributions;
}

module.exports = function(PluginManager) {
  PluginManager.registerDecider(EQUAL_PRIORITY, pluginDeciderEqualPriority);
};

if (__TEST__) {
  module.exports.plugin = pluginDeciderEqualPriority;
  module.exports.plugin.selectExperiment = selectExperiment;
  module.exports.plugin.getAllocatedWeights = getAllocatedWeights;
}

